import { useEffect, useRef, useState } from 'react';
import { api, type DocumentSearchResponse, type DocumentSearchResult } from '../api/client.js';
import { useT } from '../state/i18n.js';

/** Search history is private. Sharing a correction is an explicit separate choice. */
export function DocumentSearch({
  uploaded = [],
}: {
  uploaded?: Array<{ id: string; name: string }>;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<DocumentSearchResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [opened, setOpened] = useState<{ title: string; text: string; truncated: boolean } | null>(
    null,
  );
  const [question, setQuestion] = useState('');
  const [note, setNote] = useState('');
  const [shared, setShared] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const feedbackRequest = useRef<{ fingerprint: string; id: string } | null>(null);
  const requestSeq = useRef(0);
  const preview = useRef<HTMLElement | null>(null);
  useEffect(() => { if (opened) preview.current?.focus(); }, [opened]);
  const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

  async function search(cursor?: string) {
    const seq = ++requestSeq.current;
    setBusy(true);
    setError('');
    setOpened(null);
    try {
      const r = await api.searchDocuments(cursor && result ? result.query : query, cursor);
      if (seq !== requestSeq.current) return;
      setResult(r);
      setQuestion(r.query);
      setNote('');
      setSaved(false);
      setShared(false);
      setSelectedIds([]);
      feedbackRequest.current = null;
    } catch (e) {
      if (seq === requestSeq.current) {
        setError(message(e));
        setResult(null);
      }
    } finally {
      if (seq === requestSeq.current) setBusy(false);
    }
  }
  async function read(r: DocumentSearchResult) {
    if (!result) return;
    const seq = ++requestSeq.current;
    setBusy(true);
    setError('');
    setOpened(null);
    try {
      const body = await api.readSearchResult(r, result.searchId);
      if (seq === requestSeq.current) setOpened({ ...body, title: r.title });
    } catch (e) {
      if (seq === requestSeq.current) setError(message(e));
    } finally {
      if (seq === requestSeq.current) setBusy(false);
    }
  }
  async function sendFeedback() {
    if (!result) return;
    const payload = {
      searchId: result.searchId,
      question: question.trim(),
      note: note.trim(),
      shared,
      documentIds: selectedIds,
    };
    const fingerprint = JSON.stringify(payload);
    if (feedbackRequest.current?.fingerprint !== fingerprint)
      feedbackRequest.current = { fingerprint, id: crypto.randomUUID() };
    setBusy(true);
    setError('');
    try {
      await api.searchFeedback({ ...payload, requestId: feedbackRequest.current.id });
      setSaved(true);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="lake-search" aria-label={t({ ko: '본문 검색', en: 'Content search' })}>
      <h2>{t({ ko: '내용으로 찾기', en: 'Search the contents' })}</h2>
      <p className="muted small">
        {t({
          ko: '본문과 디스크립터에서 키워드를 찾습니다. 두 글자 이상, 공백으로 나눈 모든 단어가 같은 항목에 있어야 합니다. 검색어·결과·열람 기록이 저장됩니다.',
          en: 'Search text and descriptors using keywords of at least two characters. All words must match the same item. Queries, results and reads are recorded.',
        })}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
        className="lake-search__form"
      >
        <input
          className="ds-control"
          aria-label={t({ ko: '본문 검색어', en: 'Content keywords' })}
          value={query}
          maxLength={500}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t({ ko: '예: 임대료 VAT', en: 'e.g. rent VAT' })}
        />
        <button className="ds-button ds-button--primary" disabled={busy || !query.trim()}>
          {t({ ko: '내용 검색', en: 'Search contents' })}
        </button>
      </form>
      {busy && <p role="status">{t({ ko: '처리 중…', en: 'Working…' })}</p>}
      {error && <p role="alert">{error}</p>}
      {result && (
        <>
          <p className="muted small">
            {t({ ko: '이번 페이지', en: 'This page' })}: {result.results.length}{' '}
            {t({ ko: '항목 · 관련도순 정렬은 아닙니다', en: 'items · not ranked by relevance' })}
          </p>
          {!result.results.length && (
            <p>
              {t({
                ko: '일치하는 내용이 없습니다. 미처리 파일이나 추출하지 못한 내용이 있을 수 있으니 다른 키워드와 아래 파일 목록도 확인해 주세요.',
                en: 'No matches. Some files may be unprocessed or unreadable; try other keywords and the file list below.',
              })}
            </p>
          )}
          <ul className="lake-search__results">
            {result.results.map((r) => (
              <li key={r.descriptorId}>
                <button type="button" className="link" disabled={busy} onClick={() => void read(r)}>
                  {r.title}
                </button>
                <div className="muted small">
                  {r.sourceRef || r.documentId} ·{' '}
                  {r.page ? `p${r.page}` : t({ ko: '문서', en: 'Document' })} ·{' '}
                  {r.kind === 'text.page'
                    ? t({ ko: '추출 본문', en: 'Extracted text' })
                    : t({ ko: '자료 설명', en: 'Description' })}{' '}
                  · {r.producer}
                </div>
                <p>{r.snippet}</p>
              </li>
            ))}
          </ul>
          {result.nextCursor && (
            <button
              type="button"
              className="ds-button"
              disabled={busy}
              onClick={() => void search(result.nextCursor!)}
            >
              {t({ ko: '다음 결과', en: 'Next results' })}
            </button>
          )}
          {opened && (
          <article className="lake-search__preview" ref={preview} tabIndex={-1} aria-label={opened.title}>
              <h3>{opened.title}</h3>
              <pre>{opened.text}</pre>
              {opened.truncated && (
                <p>
                  {t({
                    ko: '긴 내용의 앞부분만 표시합니다.',
                    en: 'Only the beginning of this long item is shown.',
                  })}
                </p>
              )}
            </article>
          )}
          <details className="lake-search__feedback">
            <summary>
              {t({
                ko: '못 찾았거나 잘못된 내용이 있나요?',
                en: 'Missing or incorrect information?',
              })}
            </summary>
            {saved ? (
              <p role="status">
                {t({
                  ko: '개선 요청을 기록했습니다. 정답 확정이나 자동 수정은 아닙니다.',
                  en: 'Feedback recorded for review; this does not confirm or automatically correct an answer.',
                })}
              </p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void sendFeedback();
                }}
              >
                <label>
                  {t({ ko: '원래 질문', en: 'Original question' })}
                  <textarea
                    className="ds-control"
                    required
                    maxLength={4000}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    disabled={busy}
                  />
                </label>
                <label>
                  {t({ ko: '부족한 점·정정 내용', en: 'What is missing or incorrect' })}
                  <textarea
                    className="ds-control"
                    required
                    maxLength={4000}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={busy}
                  />
                </label>
                {!!uploaded.length && (
                  <fieldset>
                    <legend>
                      {t({
                        ko: '이번에 업로드한 참고 자료 연결',
                        en: 'Attach a file uploaded in this visit',
                      })}
                    </legend>
                    {[...new Map(uploaded.map((d) => [d.id, d])).values()].map((d) => (
                      <label key={d.id}>
                        <input
                          type="checkbox"
                          disabled={busy}
                          checked={selectedIds.includes(d.id)}
                          onChange={(e) =>
                            setSelectedIds((prev) =>
                              e.target.checked ? [...prev, d.id] : prev.filter((id) => id !== d.id),
                            )
                          }
                        />{' '}
                        {d.name}
                      </label>
                    ))}
                  </fieldset>
                )}
                <label>
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={shared}
                    onChange={(e) => setShared(e.target.checked)}
                  />{' '}
                  {t({
                    ko: '질문과 메모를 워크스페이스에 공유하여 Company Brain의 개선 자료로 제공',
                    en: 'Share this question and note with the workspace for Company Brain improvements',
                  })}
                </label>
                <p className="muted small">
                  {t({
                    ko: '체크하지 않으면 본인만 조회할 수 있습니다. 연결한 파일의 접근 권한은 바뀌지 않습니다.',
                    en: 'Unchecked feedback stays private. Attached file permissions do not change.',
                  })}
                </p>
                <button className="ds-button" disabled={busy || !note.trim() || !question.trim()}>
                  {t({ ko: '개선 요청 기록', en: 'Record feedback' })}
                </button>
              </form>
            )}
          </details>
        </>
      )}
    </section>
  );
}
